import React, { useRef, useState } from 'react';
import { ClassroomDetailProps } from '../types';
import './ClassroomDetail.css';
import SmartcareSignInButton from './SmartcareSignInButton';
import ClassroomTransferButton from './ClassroomTransferButton';
import StudentTransferButton from './StudentTransferButton';
import TeacherSearchDialog from './TeacherSearchDialog';
import TooltipComponent from './TooltipComponent';

const ClassroomDetail: React.FC<ClassroomDetailProps> = ({ classroom }) => {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const studentListRef = useRef<HTMLUListElement>(null);
  const { 
    id,
    name, 
    studentCount, 
    students, 
    teachers, 
    activeTeacherCount, 
    ratio, 
    requiredRatio, 
    displayRatio, 
    ratioStatus 
  } = classroom;

  // Check if any teacher needs to sign into SmartCare or is missing ID
  const hasTeachersNotSignedIn = teachers.some(teacher => teacher.sc_id && !teacher.sc_checkedin);
  const hasTeachersMissingId = teachers.some(teacher => !teacher.sc_id);
  // Check if any teacher is not clocked into WhenIWork
  const hasTeachersNotClockedIn = teachers.some(teacher => teacher.wiw_id && !teacher.wiw_clockedin);

  // Prevent scroll propagation when the student list is at top or bottom boundaries
  const handleWheel = (e: React.WheelEvent<HTMLUListElement>) => {
    const element = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = element;
    
    // Check if scroll has reached top or bottom
    if ((scrollTop === 0 && e.deltaY < 0) || 
        (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0)) {
      e.preventDefault();
    }
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSearchDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsSearchDialogOpen(false);
  };

  return (
    <div className="classroom-detail-component classroom-detail">
      <div className={`classroom-header status-bg-${ratioStatus}`}>
        <div className="classroom-header-content">
          <h3 className="classroom-name">{name}</h3>
          <div className="classroom-quick-stats">
            <span className="quick-stat">{studentCount} S</span>
            <span className="quick-stat">
              {activeTeacherCount} T
              {teachers.some(teacher => 
                teacher.smartcareLocation && teacher.smartcareLocation !== id
              ) && "*"}
            </span>
            <span className="quick-stat">{displayRatio}</span>
          </div>
        </div>
      </div>

      <div className="classroom-body">
        <div className="classroom-section">
          <h4 className="section-header">
            <div className="student-name-with-icon">
              <span className="student-name-text">Students ({studentCount})</span>
              {students.length > 0 && (
                <StudentTransferButton 
                  students={students}
                  currentClassroomId={id} 
                />
              )}
            </div>
          </h4>
          {students.length > 0 ? (
            <ul className="list" 
                ref={studentListRef} 
                onWheel={handleWheel}>
              {students.map((student) => (
                <li key={student.id} className="list-item">
                  <div className="student-name-with-icon">
                    <span className="student-name-text">{student.name}</span>
                    <StudentTransferButton 
                      students={[student]} 
                      currentClassroomId={id} 
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-message">No students checked in</p>
          )}
        </div>

        <div className="classroom-section">
          <h4 className="section-header">
            <div className="header-with-icon">
              <span>Teachers ({activeTeacherCount})</span>
              <span 
                className="add-teacher-icon" 
                title="Add a teacher to this classroom"
                onClick={handleSearchClick}
              />
            </div>
          </h4>
          {teachers.length > 0 ? (
            <>
              <ul className="list">
                {teachers.map((teacher) => (
                  <li key={teacher.sc_id || teacher.wiw_id} className={`list-item ${teacher.onBreak ? 'on-break' : ''}`}>
                    <span className="name">
                      {teacher.name}
                      {teacher.smartcareLocation && teacher.smartcareLocation !== id && 
                       <span className="location-mismatch"> ({teacher.smartcareLocation})</span>}
                      {teacher.wiw_shift && (
                        <TooltipComponent 
                          text={`🕛`}
                          tooltipText={`${new Date(teacher.wiw_shift.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(teacher.wiw_shift.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}                    
                        />

                      )}
                      {!teacher.sc_id && 
                       <span className="missing-id-icon" title="Does not exist in Smartcare"></span>}
                      {teacher.sc_id && !teacher.sc_checkedin && 
                       <SmartcareSignInButton teacher={teacher} currentClassroomId={id} />}
                      {teacher.wiw_id && !teacher.wiw_clockedin && 
                       <span className="not-clocked-icon" title="Teacher is not clocked in to WhenIWork"></span>}
                      <ClassroomTransferButton 
                        teacher={teacher} 
                        currentClassroomId={id} 
                      />
                    </span>
                    {teacher.onBreak && (
                      <span className="break-tag">ON BREAK</span>
                    )}
                  </li>
                ))}
              </ul>
              
              {(hasTeachersNotSignedIn || hasTeachersMissingId || hasTeachersNotClockedIn) && (
                <div className="tooltip-legend">
                  {hasTeachersNotSignedIn && (
                    <>
                      <span className="signin-icon-button"></span>
                      <span className="legend-text">Click to sign user into Smartcare</span>
                    </>
                  )}
                  {hasTeachersNotSignedIn && (hasTeachersMissingId || hasTeachersNotClockedIn) && <br />}
                  {hasTeachersMissingId && (
                    <>
                      <span className="missing-id-icon"></span>
                      <span className="legend-text">Does not exist in Smartcare</span>
                    </>
                  )}
                  {hasTeachersMissingId && hasTeachersNotClockedIn && <br />}
                  {hasTeachersNotClockedIn && (
                    <>
                      <span className="not-clocked-icon"></span>
                      <span className="legend-text">Teacher is not clocked in to WhenIWork</span>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="empty-message">No teachers assigned</p>
          )}
        </div>
      </div>
      
      {isSearchDialogOpen && (
        <TeacherSearchDialog 
          onClose={handleCloseDialog} 
          targetClassroomId={id} 
        />
      )}
    </div>
  );
};

export default ClassroomDetail;